import { PlayerService } from "./PlayerService";

/**
 * Service class responsible for managing character profile-related API calls.
 * This service provides methods to fetch a list of characters, individual character profiles,
 * and detailed character profiles with additional player session information.
 */
export class CharacterProfileService {
    /**
     * Retrieves a list of characters with their aliases and names.
     *
     * @returns {Promise<{ alias: string; name: string }[]>} A promise that resolves to an array of character objects.
     * @throws Will log an error and return an empty array if the fetch operation fails.
     */
    public async getCharacterList(): Promise<{ alias: string; name: string }[]> {
        try {
            const response: Response = await fetch(`${VITE_API_URL}elena/character-profiles/char-info`);
            return (await response.json()) as { alias: string; name: string }[];
        }
        catch (error) {
            console.error("Error fetching character list:", error);
            return [];
        }
    }

    /**
     * Retrieves the profile of a specific character by their alias.
     *
     * @param alias - The alias of the character whose profile is to be fetched.
     * @returns {Promise<{ title: string; description: string; image: string }>} A promise that resolves to an object containing the character's title, description, and image.
     * @throws Will log an error and return a default error object if the fetch operation fails.
     */
    public async getCharacterProfile(alias: string): Promise<{ title: string; description: string; image: string }> {
        try {
            const response: Response = await fetch(`${VITE_API_URL}elena/character-profiles/char/${alias}`);
            return (await response.json()) as { title: string; description: string; image: string };
        }
        catch (error) {
            console.error("Error fetching character profile:", error);
            return { title: "Error", description: "<p>Failed to load character profile.</p>", image: "<p>Cannot find image.</p>" };
        }
    }

    /**
     * Retrieves a detailed profile of a specific character, including player session information.
     *
     * @param alias - The alias of the character whose detailed profile is to be fetched.
     * @param playerSessionId - The session ID of the player making the request.
     * @returns {Promise<{ title: string; description: string }>} A promise that resolves to an object containing the character's detailed title and description.
     * @throws Will log an error and return a default error object if the fetch operation fails.
     */
    public async getDetailedCharacterProfile(alias: string): Promise<{ title: string; description: string }> {
        const playerService: PlayerService = new PlayerService();
        try {
            const playerSessionId: string | null = localStorage.getItem("playerSessionId");

            if (!playerSessionId) {
                throw new Error("No player session ID found.");
            }

            const response: Response = await fetch(`${VITE_API_URL}elena/character-profiles/char-detail`, {
                method: "GET",
                headers: {
                    "Content-Type": "application/json",
                    "X-PlayerSessionId": playerService.getPlayerSessionId(),
                    "charAlias": alias,
                },
            });

            if (!response.ok) {
                throw new Error(`HTTP error! Status: ${response.status}`);
            }
            return (await response.json()) as { title: string; description: string }; // Convert the response to a JSON object
        }
        catch (error) {
            console.error("Error fetching detailed character profile:", error);
            return { title: "Error", description: "<p>Failed to load detailed character profile.</p>" };
        }
    }
}
